import browser from "webextension-polyfill";
import * as ewe from "@eyeo/webext-ad-filtering-solution";
import * as logger from "../../utilities/background";
import { Prefs } from "~/alias/prefs";
import { CommandEventType, CommandName, CommandVersion, commandStorageKey, DeleteEventType, maximumProcessableCommands, } from "./command-library.types";
import { isDeleteBehavior, setDeleteCommandHandler } from "./delete-commands";
import { recordEvent, recordGenericEvent } from "./event-recording";
import { isValidDate } from "./param-validator";
import { checkLanguage } from "./language-check";
const knownCommandsList = Object.values(CommandName);
const actorByCommandName = new Map();
const unexecutableCommands = new Map();
function isCommand(candidate) {
    return (typeof candidate === "object" &&
        candidate !== null &&
        "version" in candidate &&
        "ipm_id" in candidate &&
        "command_name" in candidate &&
        "expiry" in candidate);
}
export function isCommandMap(candidate) {
    return (typeof candidate === "object" &&
        candidate !== null &&
        Object.keys(candidate).every((key) => typeof key === "string") &&
        Object.values(candidate).every(isCommand));
}
export function setCommandActor(commandName, actor) {
    actorByCommandName.set(commandName, actor);
    retryExecuteCommands(commandName);
}
export function dismissCommand(ipmId) {
    const command = getCommand(ipmId);
    if (!command) {
        return;
    }
    const commandStorage = Prefs.get(commandStorageKey);
    delete commandStorage[command.ipm_id];
    Prefs.set(commandStorageKey, commandStorage);
}
export function getBehavior(ipmId) {
    const command = getCommand(ipmId);
    if (!command) {
        return null;
    }
    const actor = actorByCommandName.get(command.command_name);
    if (!actor) {
        return null;
    }
    return actor.getBehavior(command);
}
export function getCommand(ipmId) {
    const commandStorage = Prefs.get(commandStorageKey);
    return commandStorage[ipmId] || null;
}
export function getStoredCommandIds() {
    const commandStorage = Prefs.get(commandStorageKey);
    if (!isCommandMap(commandStorage)) {
        return [];
    }
    return Object.keys(commandStorage);
}
export function getContent(ipmId) {
    const command = getCommand(ipmId);
    if (!command) {
        return null;
    }
    const actor = actorByCommandName.get(command.command_name);
    if (!actor) {
        return null;
    }
    return actor.getContent(command);
}
export function isCommandExpired(command) {
    if (!isValidDate(command.expiry)) {
        return true;
    }
    const expiryDateOnly = new Date(command.expiry);
    const nowDateTime = new Date();
    if (nowDateTime >= expiryDateOnly) {
        return true;
    }
    return false;
}
function retryExecuteCommands(commandName) {
    for (const [command, isInitialization] of unexecutableCommands) {
        if (command.command_name !== commandName) {
            continue;
        }
        unexecutableCommands.delete(command);
        void executeIPMCommands([command], isInitialization);
    }
}
function storeCommands(commands) {
    let changed = false;
    const storage = Prefs.get(commandStorageKey);
    for (const command of commands) {
        if (storage[command.ipm_id]) {
            logger.debug("[ipm]: Campaign already stored:", command.ipm_id);
            continue;
        }
        storage[command.ipm_id] = command;
        changed = true;
    }
    if (changed) {
        void Prefs.set(commandStorageKey, storage);
    }
}
export async function executeIPMCommands(commands, isInitialization = false) {
    const actorByExecutableCommand = new Map();
    if (commands.length > maximumProcessableCommands) {
        logger.error("[ipm]: Too many commands received.");
        recordGenericEvent("too_many_commands");
        return;
    }
    for (const command of commands) {
        if (!isCommand(command)) {
            logger.error("[ipm]: Invalid command received.");
            continue;
        }
        if (!knownCommandsList.includes(command.command_name)) {
            logger.error("[ipm]: Unknown command name received:", command.command_name);
            continue;
        }
        if (command.version !== CommandVersion[command.command_name]) {
            logger.error(`[ipm]: Command version mismatch for command "${command.command_name}". Requested version was ${command.version}, version present is ${CommandVersion[command.command_name]}`);
            continue;
        }
        if (isCommandExpired(command)) {
            logger.error("[ipm]: Command has expired.");
            recordEvent(command.ipm_id, command.command_name, CommandEventType.expired);
            if (isInitialization) {
                dismissCommand(command.ipm_id);
            }
            continue;
        }
        const actor = actorByCommandName.get(command.command_name);
        if (!actor) {
            logger.warn("[ipm]: No actor found:", command.command_name);
            unexecutableCommands.set(command, isInitialization);
            continue;
        }
        if (!actor.isValidCommand(command)) {
            logger.error("[ipm]: Invalid parameters received.");
            continue;
        }
        if (!("attributes" in command)) {
            command.attributes = {
                received: Date.now(),
                language: browser.i18n.getUILanguage(),
            };
        }
        actorByExecutableCommand.set(command, actor);
    }
    const executableCommands = Array.from(actorByExecutableCommand.keys());
    if (!isInitialization && executableCommands.length > 0) {
        storeCommands(executableCommands);
    }
    const commandHandlingPromises = Array.from(actorByExecutableCommand).map(async ([executableCommand, actor]) => {
        await actor.handleCommand(executableCommand.ipm_id, isInitialization);
    });
    void Promise.all(commandHandlingPromises).then(() => {
        knownCommandsList
            .map((name) => actorByCommandName.get(name))
            .forEach((actor) => actor === null || actor === void 0 ? void 0 : actor.onCommandsProcessed());
    });
}
export async function removeAllCommands(name) {
    await Prefs.untilLoaded;
    const commandStorage = Prefs.get(commandStorageKey);
    for (const command of Object.values(commandStorage)) {
        if (isCommand(command) && name === command.command_name) {
            logger.debug("[ipm]: removing command ", command.ipm_id, command.command_name);
            recordEvent(command.ipm_id, command.command_name, CommandEventType.ipmCancelled);
            dismissCommand(command.ipm_id);
        }
    }
}
function registerDeleteEvent(ipmId, name) {
    recordEvent(ipmId, CommandName.deleteCommands, name);
}
async function handleDeleteCommand(ipmId) {
    const command = getCommand(ipmId);
    if (!command) {
        return;
    }
    const behavior = getBehavior(ipmId);
    if (!isDeleteBehavior(behavior)) {
        logger.error("[delete-commands]: Invalid command behavior.");
        return;
    }
    void checkLanguage(ipmId);
    if (isCommandExpired(command)) {
        logger.error("[delete-commands]: Command has expired.");
        registerDeleteEvent(ipmId, CommandEventType.expired);
        dismissCommand(ipmId);
        return;
    }
    const { commandIds } = behavior;
    let success = true;
    for (const commandId of commandIds) {
        try {
            dismissCommand(commandId);
            if (getCommand(commandId) !== null) {
                throw new Error("Command was not successfully deleted.");
            }
        }
        catch (error) {
            success = false;
            logger.error("[delete-commands]: Error trying to delete command with ID ", commandId);
        }
    }
    registerDeleteEvent(ipmId, success ? DeleteEventType.sucess : DeleteEventType.error);
    dismissCommand(ipmId);
}
async function resetIPMCommands() {
    await Prefs.untilLoaded;
    const ipmCommandsReset = Prefs.get("ipm_commands_second_reset_completed");
    if (!ipmCommandsReset) {
        await Prefs.set(commandStorageKey, {});
        await Prefs.set("ipm_commands_second_reset_completed", true);
    }
}
async function start() {
    await Prefs.untilLoaded;
    await resetIPMCommands();
    ewe.experiments.onChanged.addListener(async () => {
        await resetIPMCommands();
    });
    setDeleteCommandHandler(handleDeleteCommand);
    const commandStorage = Prefs.get(commandStorageKey);
    if (!isCommandMap(commandStorage)) {
        return;
    }
    const commands = Object.values(commandStorage);
    await executeIPMCommands(commands, true);
}
start().catch(logger.error);
